import { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";

/* ═══════════════════════════════════════════════════════════════
   DraggableModal
   - Width: 98vw capped at 2600px (initialWidth prop is accepted but
     ignored — every modal in the app follows this one unified spec,
     see MAX_WIDTH/WIDTH_RATIO below)
   - Height: calc(100vh - 60px) — 40px top + 20px bottom margin
   - Drag from title bar (clamped to viewport)
   - Resize from right / bottom / br corner (min 400x300)
   - Maximize / restore (double-click titlebar)
   - Closes only via X or via children calling onClose (no click-outside)
   - Header + footer are sticky (children layout: body uses flex:1 overflow:auto,
     footer is a sibling div with natural height)
   ═══════════════════════════════════════════════════════════════ */

const MIN_W = 400;
const MIN_H = 300;
/* 2026-10-22, Arturo: "las ventanas reducelas de lo alto de manera que
   no obstruya la barra de menus principal... debe abrirse debajo de la
   barra donde aparece ground-fresh, welcome arturo, fecha, semana,
   etc" — TOP_MARGIN was 40px, well short of clearing BOTH header bars
   (.top-nav, 64px tall + sticky, then .main-header-h below it — ~10px
   top padding + ~12px bottom padding + its content + 8px margin-bottom,
   roughly another 48px) — every modal used to open overlapping the
   bottom ~24px of the top-nav. 118px clears both bars with a small
   safety margin; raising this (instead of touching height directly)
   both moves the window down AND shrinks it, since height is computed
   as the viewport space remaining below TOP_MARGIN (see `h` below). */
const TOP_MARGIN = 118;
const BOTTOM_MARGIN = 20;
/* 2026-10-20, Arturo: "al abrir cualquier embarque, bill, o ventana en
   modo edicion o no edicion, deben abrirse mas anchas, de extremo a
   extremo de la pantalla" — every modal in the app sizes off these two
   constants (initialWidth/initialHeight props are accepted but
   ignored, see below), so raising WIDTH_RATIO close to 1 and MAX_WIDTH
   well past any realistic monitor width makes every window open
   edge-to-edge instead of the old 85vw/1500px-capped standard. */
/* 2026-10-22, Arturo: "reduce las ventanas al abrirse de bills, facturas
   etc en un 5%" — both knobs scaled down 5% from the 2026-10-20
   edge-to-edge spec (2600 → 2470, 0.98 → 0.931). Still the single place
   that controls every modal's width per REGLA DE VENTANAS — never
   adjust per-file. */
const MAX_WIDTH = 2470;
const WIDTH_RATIO = 0.931;

export default function DraggableModal({
  title,
  /* Optional node rendered in the title bar, between the title text
     and the maximize/close controls (2026-10-16, BillRegistry.js's
     DetailPanel: "sales report, create invoice, edit y todo lo de esa
     seccion debe ir en la pestaña superior de la ventana junto al
     cuadro de minimizar y a la x de cerrar, antes" — moves a modal's
     own action buttons out of its body and into the window chrome
     itself). Wrapped in .dm-title-actions, which startDrag already
     ignores (same as .dm-btn) so clicking a button here never starts
     a window drag. */
  titleBarActions,
  /* 2026-10-17, Arturo (ShipmentRegistry.js's view-mode DetailPanel):
     "a la barra de titulo del lado izquierdo sube los globos que estan
     debajo de shipper" — titleBarActions already exists but its
     wrapper div has marginLeft:"auto", which shoves it all the way
     right next to the maximize/close controls. That's the right spot
     for action buttons (Preview/Print/⋮/Edit stayed there per Arturo's
     own correction — "dejalos donde estan"), but wrong for content
     that should read as part of the title itself (shipper name +
     status/week/season badges). titleLeftActions renders right after
     the title text, no auto margin, so it reads left-to-right as an
     extension of the title. Optional — omitted everywhere else. */
  titleLeftActions,
  onClose,
  children,
  /* Default zIndex sits above the navbar (z-index: 9000) and its
     dropdowns (z-index: 10000). Without this, the sticky navbar
     covered the modal's title bar — hiding the X / maximize buttons
     behind the top 24px of nav. */
  zIndex = 10001,
  /* When true, the modal mounts in maximized (100vw × 100vh) state.
     The user can still toggle back to the unified 85vw / vh-60 size
     via the maximize button — savedRectRef is seeded with that
     default so the restore action works. */
  defaultMaximized = false,
  /* initialWidth / initialHeight props (passed by nearly every caller)
     are accepted via ...rest below and never read — all modals follow
     the unified 85vw / vh-60 spec unless they opt into `compact`. */
  /* 2026-10-20, Arturo (BillRegistry.js's new Fee/Credit edit dialog):
     "no cambien los colores aun"... and separately, on sizing: a 5-6
     field form dialog looks wrong filling 98% of the screen like a
     record window. `compact` opts a SINGLE modal instance out of the
     unified edge-to-edge spec above — everyone else keeps following
     it untouched. compactWidth/compactHeight are the fixed centered
     size used instead (clamped to the viewport on small screens,
     same as the unified spec already does). */
  compact = false,
  compactWidth = 520,
  compactHeight = 600,
  /* 2026-10-17, Arturo: "la app esta demasiado blanca... necesito veas
     la pagina de ground fresh" — real institutional colors pulled live
     from ground-fresh.com (navy #122840 / gold #f0ae2a+#d4930a).
     Testing scoped to ONE module first ("vamos a hacer solo embarques
     para probar como se ve", "no des push hasta primero ver
     localmente") — titleBarVariant lets a caller opt a single
     DraggableModal instance into the new look via a CSS class
     (dm-titlebar--<variant>, see App.css) without touching the shared
     default every other module's modal still uses. Omit/undefined =
     unchanged behavior everywhere else. */
  titleBarVariant,
  ...rest
}) {
  const windowRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [pos, setPos] = useState({ x: 0, y: TOP_MARGIN });
  const [size, setSize] = useState({ w: 800, h: 600 });
  const [isMax, setIsMax] = useState(false);
  const savedRectRef = useRef(null);

  /* Initial center + size on mount */
  useEffect(() => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const w = compact ? Math.min(compactWidth, vw - 24) : Math.min(MAX_WIDTH, Math.round(vw * WIDTH_RATIO));
    const h = compact ? Math.min(compactHeight, vh - TOP_MARGIN - BOTTOM_MARGIN) : vh - TOP_MARGIN - BOTTOM_MARGIN;
    const x = Math.max(12, Math.round((vw - w) / 2));
    setSize({ w, h });
    setPos({ x, y: TOP_MARGIN });
    /* Auto-fullscreen on mobile viewports (<768px). A 600-px-wide
       phone trying to render an 800-px modal hangs half off-screen
       and the user can't reach the resize handles to fix it. Same
       isMax mechanism as `defaultMaximized` — savedRectRef seeded
       with the calc'd default so the user can still restore via the
       maximize button. */
    const isMobile = vw < 768;
    if (defaultMaximized || isMobile) {
      savedRectRef.current = { x, y: TOP_MARGIN, w, h };
      setIsMax(true);
    }
    setReady(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Drag — works in normal AND maximized state. Dragging while
     maximized snaps the modal out of max and pins it under the
     cursor so the gesture feels continuous (Windows / macOS
     pattern). The restored size comes from savedRectRef (seeded
     either by the last manual maximize OR at mount when
     defaultMaximized) so the restore reproduces the user's prior
     non-max layout. */
  const startDrag = useCallback((e) => {
    if (e.target.closest(".dm-btn") || e.target.closest(".dm-title-actions")) return;
    e.preventDefault();
    const startX = e.clientX;
    const startY = e.clientY;
    let startPosX, startPosY, winW, winH;
    if (isMax) {
      const vw = window.innerWidth;
      const def = (function () {
        const w = compact ? Math.min(compactWidth, vw - 24) : Math.min(MAX_WIDTH, Math.round(vw * WIDTH_RATIO));
        const h = compact ? Math.min(compactHeight, window.innerHeight - TOP_MARGIN - BOTTOM_MARGIN) : window.innerHeight - TOP_MARGIN - BOTTOM_MARGIN;
        return { x: Math.max(12, Math.round((vw - w) / 2)), y: TOP_MARGIN, w, h };
      })();
      const r = savedRectRef.current || def;
      winW = r.w;
      winH = r.h;
      /* Cursor stays at the same horizontal fraction of the new
         titlebar; vertically the modal aligns to top so the cursor
         remains inside the titlebar. */
      const fracX = vw > 0 ? startX / vw : 0.5;
      startPosX = Math.max(0, Math.min(vw - winW, Math.round(startX - winW * fracX)));
      startPosY = 0;
      setSize({ w: winW, h: winH });
      setPos({ x: startPosX, y: startPosY });
      setIsMax(false);
    } else {
      startPosX = pos.x;
      startPosY = pos.y;
      winW = windowRef.current ? windowRef.current.offsetWidth : size.w;
      winH = windowRef.current ? windowRef.current.offsetHeight : size.h;
    }
    const onMove = (ev) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      const maxX = Math.max(0, window.innerWidth - winW);
      const maxY = Math.max(0, window.innerHeight - winH);
      setPos({
        x: Math.max(0, Math.min(maxX, startPosX + dx)),
        y: Math.max(0, Math.min(maxY, startPosY + dy)),
      });
    };
    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  }, [pos, size, isMax, compact, compactWidth, compactHeight]);

  /* Resize */
  const startResize = useCallback((dir) => (e) => {
    if (isMax) return;
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startY = e.clientY;
    const startW = size.w;
    const startH = size.h;
    const onMove = (ev) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      setSize((s) => {
        const maxW = window.innerWidth - pos.x - 4;
        const maxH = window.innerHeight - pos.y - 4;
        return {
          w: dir.indexOf("r") >= 0 ? Math.max(MIN_W, Math.min(maxW, startW + dx)) : s.w,
          h: dir.indexOf("b") >= 0 ? Math.max(MIN_H, Math.min(maxH, startH + dy)) : s.h,
        };
      });
    };
    const onUp = () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  }, [size, pos, isMax]);

  /* Maximize toggle */
  const toggleMax = () => {
    if (isMax) {
      const r = savedRectRef.current;
      if (r) { setPos({ x: r.x, y: r.y }); setSize({ w: r.w, h: r.h }); }
      setIsMax(false);
    } else {
      savedRectRef.current = { x: pos.x, y: pos.y, w: size.w, h: size.h };
      setIsMax(true);
    }
  };

  /* Inline styles override the CSS `.dm-window` max-width: 1200 /
     max-height: calc(100vh - 60px) caps. Without these overrides the
     non-max modals at the JS MAX_WIDTH = 1500 get clipped back to 1200.

     2026-10-26, Arturo capturing a Shipment on his phone: "las ventanas
     se ven muy grandes y los espacios de abajo se hacen grandes y
     pequeños" — the maximized/mobile-auto-fullscreen state used to set
     width/height to the plain "100vh"/"100vw" STRINGS right here as an
     inline style. A single inline style object can only hold one value
     per property, so it couldn't use this app's own established
     vh→dvh fallback cascade (declare the property twice in real CSS;
     an engine that doesn't understand dvh just ignores that line and
     keeps the vh one — see the "100dvh... matches the actual visible
     viewport on iOS PWA" comments already in App.css for the exact
     same fix applied elsewhere). Plain 100vh on iOS Safari/PWA doesn't
     track the REAL visible area once the dynamic toolbar/home-indicator
     and virtual keyboard are factored in, so the window kept measuring
     itself against a size that didn't match what was actually visible
     — the loose space at the bottom growing/shrinking as those system
     UI pieces showed and hid while Arturo typed. Width/height for the
     maximized state now come from the `.dm-window.is-max` CSS class
     below instead of inline, so the vh/dvh cascade can actually apply;
     only position/radius (never contested by that class) stay inline. */
  const windowStyle = isMax
    ? { top: 0, left: 0, borderRadius: 0 }
    : { top: pos.y, left: pos.x, width: size.w, height: size.h, maxWidth: "none", maxHeight: "none" };

  /* Portal to document.body — avoids being captured by ancestor backdrop-filter,
     which would force position:fixed to anchor to .main-body and clip the modal. */
  return createPortal(
    <div className="dm-backdrop" style={{ zIndex }}>
      <div
        ref={windowRef}
        className={`dm-window ${isMax ? "is-max" : ""} ${ready ? "" : "is-init"} ${titleBarVariant ? "dm-window--" + titleBarVariant : ""}`}
        style={windowStyle}
      >
        <div
          className={`dm-titlebar${titleBarVariant ? " dm-titlebar--" + titleBarVariant : ""}`}
          onMouseDown={startDrag}
          onDoubleClick={toggleMax}
          style={{ cursor: "move" }}
        >
          <span className="dm-title">{title}</span>
          {titleLeftActions && (
            <div className="dm-title-actions" style={{ display: "flex", alignItems: "center", gap: 8, marginLeft: 10, cursor: "default", overflow: "hidden", flex: 1, minWidth: 0 }}>
              {titleLeftActions}
            </div>
          )}
          {titleBarActions && (
            <div className="dm-title-actions" style={{ display: "flex", alignItems: "center", gap: 6, marginLeft: "auto", marginRight: 10, cursor: "default" }}>
              {titleBarActions}
            </div>
          )}
          <div className="dm-controls">
            <button
              type="button"
              className="dm-btn dm-btn-max"
              onClick={toggleMax}
              title={isMax ? "Restore" : "Maximize"}
              aria-label={isMax ? "Restore" : "Maximize"}
            >
              {isMax ? (
                <svg width="14" height="14" viewBox="0 0 14 14"><path d="M3 1 H11 V9 H9 V3 H3 Z M1 5 H9 V13 H1 Z" fill="none" stroke="currentColor" strokeWidth="1.4"/></svg>
              ) : (
                <svg width="14" height="14" viewBox="0 0 14 14"><rect x="1.5" y="1.5" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="1.4"/></svg>
              )}
            </button>
            <button
              type="button"
              className="dm-btn dm-btn-close"
              onClick={onClose}
              title="Close"
              aria-label="Close"
            >
              <svg width="14" height="14" viewBox="0 0 14 14"><path d="M2 2 L12 12 M12 2 L2 12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/></svg>
            </button>
          </div>
        </div>

        {children}

        {!isMax && (
          <>
            <div className="dm-resize dm-resize-r" onMouseDown={startResize("r")} />
            <div className="dm-resize dm-resize-b" onMouseDown={startResize("b")} />
            <div className="dm-resize dm-resize-br" onMouseDown={startResize("br")} />
          </>
        )}
      </div>
    </div>,
    document.body
  );
}
